import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Clock, Calendar, LogOut, AlertCircle, MapPin, Briefcase, FileText, CheckCircle, X, AlertTriangle, Users, RefreshCw, ArrowLeft, ChevronRight, Plus, ClipboardList, StickyNote } from 'lucide-react';
import TodaysWorkplanWidget from '../components/TodaysWorkplanWidget';
import ClockOutReceipt from '../components/ClockOutReceipt';
import CompanyLogo from '../components/CompanyLogo';
import WeatherWidget from '../components/WeatherWidget';
import ErrorBoundary from '../components/ErrorBoundary';
import QuickHomeNoteDialog from '../components/QuickHomeNoteDialog';
import TodaySummaryCard from '../components/TodaySummaryCard';
import MyScheduleCard from '../components/MyScheduleCard';
import BreakTracker from '../components/timesheet/BreakTracker';
import NeedsReview from '../components/timesheet/NeedsReview';
import axios from 'axios';
import { enqueuePunch, isNetworkError } from '../utils/offlineQueue';
import { formatTimeWithTimezone, formatDateTimeWithTimezone, getCurrentTimeInUserTimezone, resolveTimeZone } from '../utils/timezone';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../components/ui/skeleton';
import EmptyState from '../components/EmptyState';
import WelcomeTourCard from '../components/WelcomeTourCard';
import { ProjectJobSelect, errorDetailText } from '../components/ProjectJobSelect';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { user, company, token, logout, isAdmin, isSupervisor, loading } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  // All hooks must be declared before any conditional returns
  const [clockedIn, setClockedIn] = useState(false);
  const [fitForDuty, setFitForDuty] = useState(false);
  // Project is REQUIRED on every punch (Sep 2026); job optional under it.
  const [punchAttr, setPunchAttr] = useState({ projectId: null, jobId: null });
  const [todayHours, setTodayHours] = useState('0h 0m');
  const [weekHours, setWeekHours] = useState('0h 0m');
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [callbackShift, setCallbackShift] = useState(false);
  const [calloutPolicy, setCalloutPolicy] = useState(null);
  const [showWellnessCheck, setShowWellnessCheck] = useState(false);
  const [showToolboxModal, setShowToolboxModal] = useState(false);
  const [showToolboxQuestion, setShowToolboxQuestion] = useState(false);
  const [todayTopic, setTodayTopic] = useState(null);
  // Quick-note dialog (Feb 2026) — workers tap the Note tile to capture a
  // thought, optionally attach to a job, optionally forward to a teammate.
  const [showHomeNote, setShowHomeNote] = useState(false);
  const [toolboxAcknowledged, setToolboxAcknowledged] = useState(null);
  const [jobStats, setJobStats] = useState({ total: 0, todo: 0, in_progress: 0, completed: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Supervisor team management states
  const [isTeamLoading, setIsTeamLoading] = useState(false);
  const [teamClockResult, setTeamClockResult] = useState(null);
  

  // Team switching states
  const [teamInfo, setTeamInfo] = useState(null);
  const [showTeamSwitcher, setShowTeamSwitcher] = useState(false);
  const [switchingTeam, setSwitchingTeam] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [coverageType, setCoverageType] = useState('coverage');

  // Weather data for header
  const [currentWeather, setCurrentWeather] = useState(null);
  const [locationName, setLocationName] = useState(null);
  // Live clock-in metadata (Feb 2026) — populated from /api/time/dashboard.
  // `activeClockInAt` is an ISO UTC string; we derive the local time + an
  // elapsed string that re-renders every 30s via the existing currentTime ticker.
  const [activeClockInAt, setActiveClockInAt] = useState(null);

  // Check localStorage as fallback during state sync
  const storedToken = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/callout/policy`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setCalloutPolicy(r.data); if (r.data?.in_window_now) setCallbackShift(true); })
      .catch(() => {});
  }, [token]);

  // Fetch weather when location is available
  useEffect(() => {
    if (location?.latitude && location?.longitude) {
      fetchCurrentWeather();
    }
  }, [location]);

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      fetchJobStats();
      fetchTeamInfo();
      getLocation();
      
      // Periodic check for auto-timeout (every 60 seconds)
      const timeoutChecker = setInterval(() => {
        fetchDashboardData();
      }, 60000);
      
      return () => clearInterval(timeoutChecker);
    }
  }, [token]);

  const fetchJobStats = async () => {
    try {
      const response = await axios.get(`${API}/jobs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobStats(response.data);
    } catch (error) {
      console.error('Failed to fetch job stats:', error);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const fetchCurrentWeather = async () => {
    try {
      const response = await axios.get(`${API}/api/weather/forecast`, {
        params: { 
          lat: location.latitude, 
          lon: location.longitude 
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.current) {
        setCurrentWeather(response.data.current);
        // Get location name using reverse geocoding
        fetchLocationName(location.latitude, location.longitude);
      }
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      // Silently fail - weather is optional
    }
  };

  const fetchLocationName = async (lat, lon) => {
    try {
      // Use OpenStreetMap Nominatim for reverse geocoding (free)
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse`,
        {
          params: {
            lat: lat,
            lon: lon,
            format: 'json'
          },
          headers: {
            'User-Agent': 'HinesTime/1.0'
          }
        }
      );
      
      if (response.data) {
        const city = response.data.address.city || 
                     response.data.address.town || 
                     response.data.address.village ||
                     response.data.address.county;
        const state = response.data.address.state;
        setLocationName(`${city}, ${state}`);
      }
    } catch (error) {
      console.error('Failed to fetch location name:', error);
      setLocationName('Your Location');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/time/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for auto-timeout
      if (response.data.autoTimedOut && clockedIn) {
        toast({
          title: 'Auto Clocked Out',
          description: 'You have been automatically clocked out after 8 hours of work for safety compliance.',
          variant: 'default',
          duration: 10000
        });
      }
      
      setClockedIn(response.data.clockedIn);
      setActiveClockInAt(response.data.activeClockInAt || null);
      // Note: fitForDuty is NOT set from backend - it must be checked fresh every clock-in
      setTodayHours(response.data.todayHours);
      setWeekHours(response.data.weekHours);
      setActivities(response.data.activities);
      setStatsLoading(false);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setStatsLoading(false);
    }
  };

  const handleDownloadPdf = async (filename) => {
    try {
      toast({
        title: 'Downloading PDF...',
        description: 'Please wait while we prepare your document',
      });

      const response = await axios.get(
        `${API}/toolbox/pdf/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create a blob and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'safety-document.pdf';
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'PDF downloaded successfully',
      });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to download PDF file. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const fetchTodayToolbox = async () => {
    try {
      const response = await axios.get(`${API}/toolbox/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.topic) {
        setTodayTopic(response.data.topic);
      }
    } catch (error) {
      if (typeof navigator === 'undefined' || navigator.onLine) {
        console.error('Failed to fetch toolbox topic:', error);
      }
    }
  };

  const handleClockIn = () => {
    // Reset fit for duty checkbox - must be checked fresh every time
    setFitForDuty(false);
    // Fetch today's toolbox topic first
    fetchTodayToolbox();
    setShowWellnessCheck(true);
  };

  const confirmClockIn = async () => {
    if (!punchAttr.projectId) {
      toast({ title: 'Project required', description: 'Pick the project you are working on (General Work for non-project time).', variant: 'destructive' });
      return;
    }
    if (!fitForDuty) {
      toast({
        title: 'Wellness Check Required',
        description: 'Please confirm you are fit for duty before clocking in',
        variant: 'destructive'
      });
      return;
    }

    // If toolbox topic exists, show it before clocking in
    if (todayTopic) {
      setShowWellnessCheck(false);
      setShowToolboxModal(true);
      return;
    }

    // Proceed with clock-in
    await performClockIn();
  };

  const confirmToolboxRead = async () => {
    setShowToolboxModal(false);
    await performClockIn();
  };

  const performClockIn = async () => {
    setIsLoading(true);
    
    try {
      await axios.post(
        `${API}/time/clockin`,
        {
          fit_for_duty: fitForDuty,
          latitude: location?.latitude,
          longitude: location?.longitude,
          project_id: punchAttr.projectId,
          job_id: punchAttr.jobId || undefined,
          callback_shift: callbackShift || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallbackShift(false);
      
      setClockedIn(true);
      toast({
        title: '✅ Clocked in',
        description: `Started at ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
        duration: 3000,
      });

      // Workers land on their dedicated "My Day" dashboard right after clocking in
      // (safety topic → workplan → jobs → notes flow). Admins/supervisors stay on
      // the current Home page since they have a richer dashboard view already.
      if (user?.role === 'user') {
        setTimeout(() => navigate('/worker'), 600);
      }

      fetchDashboardData();
      fetchJobStats();
    } catch (error) {
      if (isNetworkError(error)) {
        // Offline mode: save the punch on-device with its tap time — it syncs
        // automatically (OfflineSyncBanner) once signal returns.
        enqueuePunch('clock_in', {
          location: location?.latitude != null ? { latitude: location.latitude, longitude: location.longitude } : null,
        });
        setClockedIn(true);
        toast({
          title: t('common.success'),
          description: "No signal — your clock-in was saved on this device and will sync automatically once you're back online.",
          duration: 8000,
        });
        if (user?.role === 'user') {
          setTimeout(() => navigate('/worker'), 600);
        }
      } else {
        toast({
          title: 'Clock In Failed',
          description: errorDetailText(error, error.response?.data?.message || t('message.error.clockIn')),
          variant: 'destructive',
          duration: 8000
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockOut = async () => {
    // If there was a toolbox topic today, ask for acknowledgment
    if (todayTopic) {
      setShowToolboxQuestion(true);
      return;
    }
    
    // Otherwise, clock out directly
    await performClockOut();
  };

  const confirmToolboxResponse = async (acknowledged) => {
    // Save toolbox response
    try {
      await axios.post(
        `${API}/toolbox/respond`,
        {
          topic_id: todayTopic.id,
          acknowledged: acknowledged
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save toolbox response:', error);
    }
    
    setShowToolboxQuestion(false);
    await performClockOut();
  };

  const [showMealBreakPrompt, setShowMealBreakPrompt] = useState(false);
  const [mealBreakHours, setMealBreakHours] = useState(null);
  const [mealBreakThreshold, setMealBreakThreshold] = useState(5);
  const [clockOutReceipt, setClockOutReceipt] = useState(null);

  const performClockOut = async (deductBreak = false, confirmNoBreak = false) => {
    setIsLoading(true);
    
    try {
      const params = deductBreak ? '?deduct_break=true' : confirmNoBreak ? '?confirm_no_break=true' : '';
      const url = `${API}/time/clockout${params}`;
      const resp = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Alberta ESA: 5h+ shift without a logged meal break — ask the worker.
      if (!deductBreak && !confirmNoBreak && resp.data?.meal_break_warning) {
        setMealBreakHours(resp.data.shift_hours || null);
        setMealBreakThreshold(resp.data.break_threshold_hours || 5);
        setShowMealBreakPrompt(true);
        return;
      }
      setShowMealBreakPrompt(false);
      
      setClockedIn(false);
      setActiveClockInAt(null);
      setFitForDuty(false);
      setTodayTopic(null);
      setToolboxAcknowledged(null);
      setClockOutReceipt({ hours: resp.data?.hours_worked, deducted: resp.data?.meal_break_deducted });
      
      fetchDashboardData();
    } catch (error) {
      if (isNetworkError(error)) {
        enqueuePunch('clock_out', {});
        setClockedIn(false);
        setActiveClockInAt(null);
        setFitForDuty(false);
        setTodayTopic(null);
        setToolboxAcknowledged(null);
        toast({
          title: t('common.success'),
          description: "No signal — your clock-out was saved on this device and will sync automatically once you're back online.",
          duration: 8000,
        });
      } else {
        toast({
          title: 'Clock Out Failed',
          description: error.response?.data?.detail || error.response?.data?.message || t('message.error.clockOut'),
          variant: 'destructive',
          duration: 8000
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Supervisor Team Clock In
  const handleTeamClockIn = async () => {
    setIsTeamLoading(true);
    setTeamClockResult(null);
    
    try {
      const response = await axios.post(
        `${API}/time/team-clockin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTeamClockResult({
        type: 'clockin',
        ...response.data
      });
      
      toast({
        title: t('supervisor.clockInTeam'),
        description: `${response.data.clocked_in} member(s) clocked in successfully`,
      });
      
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Team Clock In Failed',
        description: error.response?.data?.detail || 'Unable to clock in team. Please try again.',
        variant: 'destructive',
        duration: 8000
      });
    } finally {
      setIsTeamLoading(false);
    }
  };

  // Supervisor Team Clock Out
  const handleTeamClockOut = async () => {
    setIsTeamLoading(true);
    setTeamClockResult(null);
    
    try {
      const response = await axios.post(
        `${API}/time/team-clockout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTeamClockResult({
        type: 'clockout',
        ...response.data
      });
      
      toast({
        title: t('supervisor.clockOutTeam'),
        description: `${response.data.clocked_out} member(s) clocked out successfully`,
      });
      
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Team Clock Out Failed',
        description: error.response?.data?.detail || 'Failed to clock out team',
        variant: 'destructive'
      });
    } finally {
      setIsTeamLoading(false);
    }
  };

  // Fetch user's current team information
  const fetchTeamInfo = async () => {
    try {
      const response = await axios.get(`${API}/users/current-team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch team info:', error);
    }
  };

  // Switch to a different team
  const handleSwitchTeam = async () => {
    if (!selectedTeam) {
      toast({
        title: 'Team Required',
        description: 'Please select a team to switch to',
        variant: 'destructive'
      });
      return;
    }

    setSwitchingTeam(true);
    try {
      const response = await axios.post(
        `${API}/users/switch-team`,
        {
          team_id: selectedTeam,
          coverage_type: coverageType,
          duration_hours: 12
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Team Switched',
        description: response.data.message
      });

      // Refresh team info
      await fetchTeamInfo();
      setShowTeamSwitcher(false);
      setSelectedTeam('');
    } catch (error) {
      toast({
        title: 'Switch Failed',
        description: error.response?.data?.detail || 'Failed to switch team',
        variant: 'destructive'
      });
    } finally {
      setSwitchingTeam(false);
    }
  };

  // Revert to permanent team
  const handleRevertTeam = async () => {
    try {
      const response = await axios.post(
        `${API}/users/revert-team`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Team Reverted',
        description: response.data.message
      });

      // Refresh team info
      await fetchTeamInfo();
    } catch (error) {
      toast({
        title: 'Revert Failed',
        description: error.response?.data?.detail || 'Failed to revert team',
        variant: 'destructive'
      });
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('home.goodMorning');
    if (hour < 18) return t('home.goodAfternoon');
    return t('home.goodEvening');
  };

  const formatDate = (date) => {
    const effectiveTimezone = resolveTimeZone(user?.timezone, date) || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
    return date.toLocaleDateString(undefined, { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: effectiveTimezone
    });
  };

  const formatTime = (date) => {
    // Get current time in user's timezone with abbreviation
    return getCurrentTimeInUserTimezone(user?.timezone || 'America/Toronto');
  };

  const celsiusToFahrenheit = (celsius) => {
    return Math.round((celsius * 9/5) + 32);
  };

  // Auth gating — kept BELOW every hook and function declaration. Placing
  // these early returns above the hooks caused "Rendered more hooks than
  // during the previous render"; placing them between the hooks and the
  // function consts caused TDZ crashes in effect closures (Jun 2026 fix).
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#1E88E5' }}></div>
          <p className="text-lg" style={{ color: 'var(--ht-text-secondary)' }}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if ((!token && !storedToken) || (!user && !storedUser)) {
    navigate('/signin');
    return null;
  }

  return (
    <div className="min-h-screen pb-4" style={{ background: 'var(--ht-bg)' }}>
      <ClockOutReceipt receipt={clockOutReceipt} onClose={() => setClockOutReceipt(null)} />
      {/* Main Content */}
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
        {/* Greeting Section */}
        <div className="mb-6">


        {/* Team Switcher - For Mutuals/Coverage */}
        {teamInfo && (
          <Card className="mb-6 border-2 border-[#2D3748] bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] shadow-xl">
            <CardHeader className="bg-gradient-to-r from-[#A78BFA] to-[#818CF8] text-white">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center space-x-2">
                  <Users className="w-6 h-6" />
                  <span>Your Team Assignment</span>
                </span>
                {teamInfo.is_on_coverage && (
                  <Badge variant="outline" className="bg-[#1A2433] text-yellow-300 border-yellow-400 font-semibold">
                    {teamInfo.coverage_type === 'mutual' ? 'Mutual' : 'Coverage'}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Current Team Display */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-[#1A2433] rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#A78BFA]" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Currently Working With:</p>
                        <p className="text-xl font-bold text-white">
                          {teamInfo.is_on_coverage ? teamInfo.working_team?.name : teamInfo.permanent_team?.name || 'No Team Assigned'}
                        </p>
                        {teamInfo.is_on_coverage && teamInfo.working_team && (
                          <p className="text-sm text-slate-400">{teamInfo.working_team.location}</p>
                        )}
                      </div>
                    </div>

                    {teamInfo.is_on_coverage && (
                      <div className="mt-3 p-3 bg-[#0F1923] border border-[#2D3748] rounded-lg">
                        <p className="text-sm text-slate-300">
                          <span className="font-semibold">Temporary Assignment:</span> Your hours will be recorded under {teamInfo.working_team?.name}
                        </p>
                        {teamInfo.permanent_team && (
                          <p className="text-sm text-slate-400 mt-1">
                            Permanent Team: {teamInfo.permanent_team.name}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Team Switch Actions */}
                <div className="flex flex-wrap gap-3 pt-3 border-t">
                  {teamInfo.is_on_coverage ? (
                    <Button
                      onClick={handleRevertTeam}
                      variant="outline"
                      className="border-purple-300 text-purple-700 hover:bg-[#0F1923]"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Return to Permanent Team
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowTeamSwitcher(!showTeamSwitcher)}
                      variant="outline"
                      className="border-purple-300 text-purple-700 hover:bg-[#0F1923]"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Work with Different Team (Mutual/Coverage)
                    </Button>
                  )}
                </div>

                {/* Team Switcher Dialog */}
                {showTeamSwitcher && !teamInfo.is_on_coverage && (
                  <div className="mt-4 p-4 bg-[#0F1923] border-2 border-[#2D3748] rounded-lg">
                    <h4 className="font-bold text-lg mb-3 text-purple-900">Switch to Different Team</h4>
                    
                    <div className="space-y-3">
                      {/* Team Selection */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-300 mb-1 block">
                          Select Team
                        </Label>
                        <select
                          value={selectedTeam}
                          onChange={(e) => setSelectedTeam(e.target.value)}
                          className="w-full px-3 py-2 border border-[#2D3748] rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Choose a team...</option>
                          {teamInfo.available_teams?.filter(t => t.id !== teamInfo.permanent_team?.id).map(team => (
                            <option key={team.id} value={team.id}>
                              {team.name} - {team.location}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Coverage Type */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-300 mb-1 block">
                          Type of Assignment
                        </Label>
                        <select
                          value={coverageType}
                          onChange={(e) => setCoverageType(e.target.value)}
                          className="w-full px-3 py-2 border border-[#2D3748] rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="coverage">Coverage (Filling In)</option>
                          <option value="mutual">Mutual (Trading Shifts)</option>
                        </select>
                      </div>

                      {/* Duration Info */}
                      <div className="p-3 bg-[#0F1923] border border-[#2D3748] rounded-lg">
                        <p className="text-sm text-slate-300">
                          <span className="font-semibold">Duration:</span> Assignment will last for 12 hours from now
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-3">
                        <Button
                          onClick={handleSwitchTeam}
                          disabled={switchingTeam || !selectedTeam}
                          className="flex-1 bg-[#A78BFA] hover:bg-purple-700 text-white"
                        >
                          {switchingTeam ? 'Switching...' : 'Confirm Switch'}
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTeamSwitcher(false);
                            setSelectedTeam('');
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

          <div className="flex items-start justify-between">
            <h2 className="text-3xl font-bold text-white mb-1">
              Hi, {user?.name?.split(' ')[0]}!
            </h2>
            <button
              onClick={() => { fetchDashboardData(); fetchJobStats(); }}
              className="p-2 rounded-lg transition-all hover:bg-white/5 active:scale-95"
              style={{ color: '#64748B' }}
              title="Refresh"
              data-testid="home-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="text-sm" style={{ color: 'var(--ht-text-secondary)' }}>
            {formatDate(currentTime)} &bull; {formatTime(currentTime)}
          </p>
          
          {/* Weather Info */}
          {currentWeather && locationName && (
            <p className="text-xs mt-1" style={{ color: 'var(--ht-text-muted)' }}>
              <MapPin className="w-3 h-3 inline mr-1" />
              {locationName} &bull; {celsiusToFahrenheit(currentWeather.temp)}°F ({currentWeather.description})
            </p>
          )}
        </div>

        {/* ===== CLOCK IN / OUT CARD (matches mobile ref) ===== */}
        <div className="mb-4 p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #1E88E5, #42A5F5)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Clock In / Out</p>
                <p className="text-white/80 text-xs" data-testid="home-clock-status">
                  {clockedIn && activeClockInAt
                    ? (() => {
                        // Show "Clocked in since 8:42 AM · 2h 14m" — recomputed
                        // every 1s because `currentTime` is a state ticker.
                        const since = new Date(activeClockInAt);
                        const sinceLabel = since.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                        const elapsedMs = Math.max(0, currentTime.getTime() - since.getTime());
                        const totalMin = Math.floor(elapsedMs / 60000);
                        const hh = Math.floor(totalMin / 60);
                        const mm = totalMin % 60;
                        const elapsed = hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
                        return `Clocked in since ${sinceLabel} · ${elapsed}`;
                      })()
                    : clockedIn
                      ? 'Clocked In'
                      : 'Not Clocked In'}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </div>

        {/* Today's Workplan — appears only if the worker has an assigned plan today.
            Hidden entirely on days/users with no workplan, so admins who don't
            work on the clock don't see an empty card. */}
        <TodaysWorkplanWidget />

        {/* ===== QUICK ACTION CARDS (matches mobile ref) ===== */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => navigate('/jobs/board')}
            className="ht-card p-4 flex flex-col items-center gap-2 text-center hover:border-[#1E88E5]/50 transition-all"
            data-testid="quick-jobs-board"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.15)' }}>
              <ClipboardList className="w-5 h-5" style={{ color: '#1E88E5' }} />
            </div>
            <span className="text-sm font-medium text-white">Jobs Board</span>
          </button>
          <button
            onClick={() => navigate('/jobs')}
            className="ht-card p-4 flex flex-col items-center gap-2 text-center hover:border-[#1E88E5]/50 transition-all"
            data-testid="quick-new-job"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,200,150,0.15)' }}>
              <Plus className="w-5 h-5" style={{ color: '#00C896' }} />
            </div>
            <span className="text-sm font-medium text-white">Start New Job</span>
          </button>
          <button
            onClick={() => navigate('/reports')}
            className="ht-card p-4 flex flex-col items-center gap-2 text-center hover:border-[#1E88E5]/50 transition-all"
            data-testid="quick-log-tasks"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.15)' }}>
              <ClipboardList className="w-5 h-5" style={{ color: '#1E88E5' }} />
            </div>
            <span className="text-sm font-medium text-white">Log Tasks</span>
          </button>
          <button
            onClick={() => setShowHomeNote(true)}
            className="ht-card p-4 flex flex-col items-center gap-2 text-center hover:border-amber-500/60 transition-all"
            data-testid="quick-note-home"
            aria-label="Take a quick note"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.18)' }}>
              <StickyNote className="w-5 h-5" style={{ color: '#F59E0B' }} />
            </div>
            <span className="text-sm font-medium text-white">Note</span>
          </button>
        </div>

        {/* Today's hours rolled up by Work Area (Feb 2026) — only shows
            when the worker has time logged or is currently clocked in. */}
        <TodaySummaryCard token={token} />

        {/* Upcoming shifts assigned to this user (Jun 2026) */}
        <MyScheduleCard />

        {/* Job Assigned Notification */}
        {jobStats.total > 0 && (
          <div
            className="ht-card p-4 mb-4 cursor-pointer hover:border-blue-500/30 active:scale-[0.99] transition-all"
            onClick={() => window.location.href = '/jobs'}
            data-testid="job-notification"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.15)' }}>
                  <Briefcase className="w-5 h-5" style={{ color: '#1E88E5' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {jobStats.total} job{jobStats.total !== 1 ? 's' : ''} assigned
                  </p>
                  <p className="text-xs" style={{ color: 'var(--ht-text-muted)' }}>
                    {jobStats.todo} to do &bull; {jobStats.in_progress} in progress
                  </p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = '/jobs'}
                className="bg-[#1E88E5] hover:bg-blue-700 text-white px-3 py-1.5 rounded-xl text-xs"
              >
                View
              </Button>
            </div>
          </div>
        )}

        {/* No jobs yet — workers only */}
        {jobStats.total === 0 && !statsLoading && !isAdmin && !isSupervisor && (
          <div className="ht-card mb-4" data-testid="no-jobs-empty-state">
            <EmptyState
              icon={Briefcase}
              title="No jobs assigned yet"
              description="Your supervisor will assign work to you here. Check back after your briefing."
            />
          </div>
        )}

        {/* Wellness Check Modal */}
        {showWellnessCheck && !clockedIn && (
          <Card className="mb-6 border-2 border-[#2D3748] bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-[#1E88E5] to-[#00C896] border-b-4 border-[#00C896]">
              <CardTitle className="flex items-center justify-center space-x-3 text-white">
                <div className="w-12 h-12 bg-[#1A2433] rounded-lg flex items-center justify-center shadow-md">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_workpulse-64/artifacts/bi85sqf7_fit%20for%20duty.png"
                    alt="Fit for Duty"
                    className="w-9 h-9 object-contain"
                  />
                </div>
                <span className="text-2xl font-bold">Wellness Check Required</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                {/* Image and Message Section */}
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-36 h-36 bg-[#1A2433] rounded-2xl flex items-center justify-center shadow-xl border border-[#2D3748]">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_workpulse-64/artifacts/bi85sqf7_fit%20for%20duty.png"
                      alt="Fit for Duty"
                      className="w-32 h-32 object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Safety First!
                    </h3>
                    <p className="text-slate-400 text-lg">
                      Before clocking in, please confirm that you are physically and mentally ready for work.
                    </p>
                  </div>
                </div>

                {/* Checkbox Section */}
                <div className="flex items-start space-x-4 p-6 bg-gradient-to-r from-blue-100 to-green-100 rounded-xl border-2 border-[#2D3748] shadow-inner hover:shadow-lg transition-shadow">
                  <Checkbox
                    id="fitForDuty"
                    checked={fitForDuty}
                    onCheckedChange={setFitForDuty}
                    className="w-7 h-7 mt-1 border-2 border-[#1E88E5] data-[state=checked]:bg-[#00C896] data-[state=checked]:border-green-600"
                  />
                  <Label 
                    htmlFor="fitForDuty" 
                    className="text-lg font-semibold cursor-pointer text-white leading-relaxed"
                  >
                    I confirm that I am <span className="text-blue-700">fit for duty</span> and ready to work <span className="text-green-700">safely</span>
                  </Label>
                </div>

                {/* Project (required) + optional job — Sep 2026 */}
                <div className="space-y-2" data-testid="home-clockin-project-block">
                  <Label className="text-sm font-semibold text-white">Which project are you working on? <span className="text-red-400">*</span></Label>
                  <ProjectJobSelect token={token} projectId={punchAttr.projectId} jobId={punchAttr.jobId} onChange={setPunchAttr} testIdPrefix="home-clockin" />
                </div>

                {/* Info Box */}
                <div className="p-4 bg-[#0F1923] border-l-4 border-[#1E88E5] rounded-r-lg space-y-2">
                  <p className="text-sm text-blue-300 flex items-start">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      This wellness check ensures you are ready and safe to work. Your confirmation helps maintain workplace safety standards.
                    </span>
                  </p>
                  <p className="text-sm font-semibold text-red-700 flex items-start ml-7">
                    <span>
                      If you are NOT fit for duty, please contact your supervisor or manager immediately to advise them of your status.
                    </span>
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-2">
                  <Button
                    onClick={confirmClockIn}
                    disabled={!fitForDuty || isLoading || !punchAttr.projectId}
                    data-testid="confirm-clock-in-btn"
                    className="flex-1 h-14 bg-gradient-to-r from-green-500 to-[#00C896] hover:from-[#00C896] hover:to-green-700 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <Clock className="w-5 h-5 mr-2 animate-spin" />
                        Clocking In...
                      </span>
                    ) : (
                      '✓ Confirm & Clock In'
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowWellnessCheck(false);
                      setFitForDuty(false);
                    }}
                    variant="outline"
                    className="flex-1 h-14 border-2 border-[#2D3748] hover:border-[#4A5568] hover:bg-[#1A2433] font-semibold text-lg"
                  >
                    Cancel
                  </Button>
                </div>
                {!fitForDuty && !clockedIn && (
                  <p className="text-xs text-center mt-2" style={{ color: '#64748B' }}>
                    ☝️ Confirm you are fit for duty above to enable clock-in
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}


        {/* Toolbox Topic Modal (shown after wellness check) */}
        {showToolboxModal && todayTopic && (
          <Card className="mb-6 border-2 border-orange-400 bg-gradient-to-br from-orange-50 via-[#0F1923] to-yellow-50 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-orange-600 to-yellow-600 border-b-4 border-[#FFA000]">
              <CardTitle className="flex items-center justify-center space-x-3 text-white">
                <AlertTriangle className="w-12 h-12" />
                <span className="text-2xl font-bold">Toolbox Meeting - {todayTopic.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="bg-[#0F1923] border-l-4 border-[#FFA000] p-4 rounded-r-lg">
                  <p className="text-sm text-yellow-900 flex items-start">
                    <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">
                      Please read today's safety topic before starting work
                    </span>
                  </p>
                </div>

                {todayTopic.content_text && (
                  <div className="bg-[#1A2433] p-6 rounded-lg border-2 border-[#2D3748]">
                    <h4 className="font-bold text-white mb-3 text-lg">Topic Content:</h4>
                    <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                      {todayTopic.content_text}
                    </p>
                  </div>
                )}

                {todayTopic.content_pdf_filename && (
                  <div className="flex items-center justify-center p-6 bg-[#0F1923] rounded-lg border-2 border-[#2D3748]">
                    <Button
                      onClick={() => handleDownloadPdf(todayTopic.content_pdf_filename)}
                      className="bg-[#1E88E5] hover:bg-blue-700"
                    >
                      <FileText className="w-5 h-5 mr-2" />
                      Download PDF Safety Document
                    </Button>
                  </div>
                )}

                <div className="flex space-x-4 pt-2">
                  <Button
                    onClick={confirmToolboxRead}
                    data-testid="toolbox-read-continue-btn"
                    className="flex-1 h-14 bg-gradient-to-r from-orange-500 to-yellow-600 hover:from-orange-600 hover:to-yellow-700 text-white font-bold text-lg"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    I Read This - Continue Clock In
                  </Button>
                  <Button
                    onClick={() => {
                      setShowToolboxModal(false);
                      setShowWellnessCheck(true);
                    }}
                    variant="outline"
                    className="flex-1 h-14"
                  >
                    Go Back
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Toolbox Question Modal (shown on clock-out) */}
        {showToolboxQuestion && todayTopic && (
          <Card className="mb-6 border-2 border-purple-400 bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-blue-50 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-[#A78BFA] to-blue-600 border-b-4 border-[#1E88E5]">
              <CardTitle className="flex items-center justify-center space-x-3 text-white">
                <AlertTriangle className="w-12 h-12" />
                <span className="text-2xl font-bold">Quick Safety Check</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Did you read today's toolbox topic?
                  </h3>
                  <p className="text-lg text-slate-400">
                    Topic: <span className="font-semibold text-white">{todayTopic.title}</span>
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={() => confirmToolboxResponse(true)}
                    disabled={isLoading}
                    className="h-20 bg-gradient-to-r from-green-500 to-[#00C896] hover:from-[#00C896] hover:to-green-700 text-white font-bold text-xl"
                  >
                    <CheckCircle className="w-8 h-8 mr-3" />
                    Yes, I Read It
                  </Button>
                  <Button
                    onClick={() => confirmToolboxResponse(false)}
                    disabled={isLoading}
                    variant="outline"
                    className="h-20 border-2 border-[#2D3748] hover:bg-[#0F1923] font-semibold text-xl"
                  >
                    <X className="w-8 h-8 mr-3" />
                    No, I Didn't
                  </Button>
                </div>

                <div className="bg-[#0F1923] border border-[#2D3748] rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Your response will be logged for safety compliance tracking. You can still clock out regardless of your answer.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Meal Break Prompt (Alberta ESA — shown on clock-out for 5h+ shifts with no logged break) */}
        {showMealBreakPrompt && (
          <Card data-testid="meal-break-prompt" className="mb-6 border-2 border-amber-400 bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-amber-50 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-600 border-b-4 border-amber-700">
              <CardTitle className="flex items-center justify-center space-x-3 text-white">
                <AlertTriangle className="w-12 h-12" />
                <span className="text-2xl font-bold">Meal Break Check</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Did you take a meal break today?
                  </h3>
                  <p className="text-lg text-slate-400">
                    You worked <span className="font-semibold text-white">{mealBreakHours ? `${mealBreakHours} hours` : `${mealBreakThreshold}+ hours`}</span> without a logged break. Your company requires a 30-minute break for shifts of {mealBreakThreshold} or more hours.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button
                    data-testid="meal-break-deduct-btn"
                    onClick={() => { setShowMealBreakPrompt(false); performClockOut(true); }}
                    disabled={isLoading}
                    className="h-20 bg-gradient-to-r from-green-500 to-[#00C896] hover:from-[#00C896] hover:to-green-700 text-white font-bold text-xl"
                  >
                    <CheckCircle className="w-8 h-8 mr-3" />
                    Yes — Deduct 30 Min
                  </Button>
                  <Button
                    data-testid="meal-break-no-break-btn"
                    onClick={() => { setShowMealBreakPrompt(false); performClockOut(false, true); }}
                    disabled={isLoading}
                    variant="outline"
                    className="h-20 border-2 border-[#2D3748] hover:bg-[#0F1923] font-semibold text-xl"
                  >
                    <X className="w-8 h-8 mr-3" />
                    No Break Taken
                  </Button>
                </div>

                <div className="bg-[#0F1923] border border-[#2D3748] rounded-lg p-4">
                  <p className="text-sm text-amber-500">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Choosing "Yes" deducts an unpaid 30-minute break from this shift. Your choice is logged for compliance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clock Card */}
        <Card className="shadow-xl border-2 mb-6 hover:shadow-2xl transition-shadow">
          <CardHeader className={`rounded-t-lg text-white ${
            clockedIn 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
              : 'bg-gradient-to-r from-[#1E88E5] to-[#42A5F5]'
          }`}>
            <CardTitle className="text-2xl font-bold flex items-center space-x-3">
              <Clock className="w-8 h-8" />
              <span>{clockedIn ? t('home.clockedIn') : t('home.readyToClock')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm mb-1" style={{ color: '#64748B' }}>Current Status</p>
                  <p className="text-3xl font-bold">
                    {clockedIn ? (
                      <span className="text-[#00C896]">Working</span>
                    ) : (
                      <span className="text-slate-400">Not Clocked In</span>
                    )}
                  </p>
                </div>
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  clockedIn ? 'bg-[#1A2433]' : 'bg-[#0F1923]'
                }`}>
                  <div className={`w-14 h-14 rounded-full ${
                    clockedIn ? 'bg-[#0F1923]0 animate-pulse' : 'bg-gray-400'
                  }`}></div>
                </div>
              </div>

              {location && (
                <div className="flex items-center space-x-2 text-sm text-slate-400 bg-[#0F1923] p-3 rounded-lg">
                  <MapPin className="w-4 h-4" />
                  <span>Location tracking enabled</span>
                </div>
              )}

              {!clockedIn && (
                <label className="flex items-center gap-3 text-sm text-slate-300 bg-[#0F1923] p-3 rounded-lg cursor-pointer" data-testid="home-callback-toggle">
                  <input
                    type="checkbox"
                    checked={callbackShift}
                    onChange={(e) => setCallbackShift(e.target.checked)}
                    className="w-4 h-4 accent-pink-400"
                  />
                  <span>After-hours call-out{calloutPolicy ? <span className="text-slate-500"> · {calloutPolicy.callout_window_start}–{calloutPolicy.callout_window_end}, min {calloutPolicy.my_min_hours}h</span> : null}</span>
                </label>
              )}

              <Button
                onClick={clockedIn ? handleClockOut : handleClockIn}
                disabled={isLoading}
                data-testid="home-clock-toggle-btn"
                className={`w-full h-14 md:h-16 text-base md:text-lg font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  clockedIn 
                    ? 'bg-[#0F1923]0 hover:bg-[#EF4444]' 
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-[#00C896] hover:to-emerald-700'
                } text-white shadow-lg`}
              >
                {isLoading ? t('common.loading') : clockedIn ? t('home.clockOut') : t('home.clockIn')}
              </Button>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate(user?.role === 'user' ? '/my-time' : '/reports?preset=today')}
                  className="bg-[#0F1923] p-4 rounded-xl border border-[#2D3748] text-left cursor-pointer hover:border-blue-500/30 active:scale-[0.98] transition-all"
                  data-testid="today-hours-tile"
                >
                  <p className="text-sm text-blue-700 font-semibold mb-1">{t('home.todayHours')}</p>
                  {statsLoading ? (
                    <Skeleton className="h-9 w-20" />
                  ) : (
                    <p className="text-3xl font-bold text-blue-300">{todayHours}</p>
                  )}
                  <p className="text-xs mt-1" style={{ color: '#475569' }}>Tap to view →</p>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(user?.role === 'user' ? '/my-time' : '/reports')}
                  className="bg-[#0F1923] p-4 rounded-xl border border-[#2D3748] text-left cursor-pointer hover:border-green-500/30 active:scale-[0.98] transition-all"
                  data-testid="week-hours-tile"
                >
                  <p className="text-sm text-green-700 font-semibold mb-1">{t('home.weekHours')}</p>
                  {statsLoading ? (
                    <Skeleton className="h-9 w-20" />
                  ) : (
                    <p className="text-3xl font-bold text-green-300">{weekHours}</p>
                  )}
                  <p className="text-xs mt-1" style={{ color: '#475569' }}>Tap to view →</p>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Break tracking (while clocked in) + shifts awaiting certification */}
        <BreakTracker token={token} clockedIn={clockedIn} />
        <NeedsReview token={token} />

        {/* Welcome tour — guided setup for brand-new companies (admins) */}
        {isAdmin && <WelcomeTourCard token={token} />}

        {/* Dashboard Button - For Admins and Supervisors */}
        {(isAdmin || isSupervisor) && (
          <Card className="shadow-xl mb-6 border-2 border-[#2D3748] hover:shadow-2xl transition-shadow">
            <CardContent className="p-6">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full h-16 bg-gradient-to-r from-[#1E88E5] to-[#42A5F5] hover:from-blue-700 hover:to-cyan-700 text-white font-semibold text-lg shadow-lg"
                data-testid="go-to-dashboard-btn"
              >
                <Briefcase className="w-6 h-6 mr-3" />
                Company Dashboard
              </Button>
              <p className="text-center text-sm text-slate-400 mt-3">
                View job stats, charts, activity feed, and export payroll
              </p>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/system-health')}
                  className="w-full mt-2 text-slate-300"
                  data-testid="system-health-link"
                >
                  System Health — QuickBooks, email &amp; background jobs at a glance
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Team Management Button - For Admins and Supervisors */}
        {(isAdmin || isSupervisor) && (
          <Card className="shadow-xl mb-6 border-2 border-[#2D3748] hover:shadow-2xl transition-shadow">
            <CardContent className="p-6">
              <Button
                onClick={() => navigate('/teams')}
                className="w-full h-16 bg-gradient-to-r from-[#A78BFA] to-[#818CF8] hover:from-purple-700 hover:to-indigo-700 text-white font-semibold text-lg shadow-lg"
              >
                <Users className="w-6 h-6 mr-3" />
                Manage Teams
              </Button>
              <p className="text-center text-sm text-slate-400 mt-3">
                {isAdmin ? 'View and manage all company teams' : 'Manage your assigned team'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Supervisor Team Management Section */}
        {isSupervisor && (
          <Card className="shadow-xl border-2 mb-6 border-[#2D3748] hover:shadow-2xl transition-shadow">
            <CardHeader className="rounded-t-lg bg-gradient-to-r from-[#A78BFA] to-[#818CF8] text-white">
              <CardTitle className="text-2xl font-bold flex items-center space-x-3">
                <Users className="w-8 h-8" />
                <span>{t('supervisor.teamManagement')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-[#0F1923] border border-[#2D3748] rounded-lg p-4">
                  <p className="text-sm text-purple-900 flex items-start">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">
                      {t('supervisor.teamDescription')}
                    </span>
                  </p>
                </div>

                {/* Team Clock Result Display */}
                {teamClockResult && (
                  <div className={`p-4 rounded-lg border-2 ${
                    teamClockResult.type === 'clockin' 
                      ? 'bg-[#0F1923] border-[#2D3748]' 
                      : 'bg-[#0F1923] border-[#2D3748]'
                  }`}>
                    <h4 className="font-bold text-white mb-2">
                      {teamClockResult.type === 'clockin' ? t('supervisor.clockInResults') : t('supervisor.clockOutResults')}
                    </h4>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-[#1A2433] rounded-lg p-3">
                        <p className="text-2xl font-bold text-white">
                          {teamClockResult.type === 'clockin' 
                            ? teamClockResult.clocked_in 
                            : teamClockResult.clocked_out}
                        </p>
                        <p className="text-xs text-slate-400">
                          {teamClockResult.type === 'clockin' ? t('supervisor.clockedIn') : t('supervisor.clockedOut')}
                        </p>
                      </div>
                      <div className="bg-[#1A2433] rounded-lg p-3">
                        <p className="text-2xl font-bold text-slate-400">
                          {teamClockResult.type === 'clockin' 
                            ? teamClockResult.already_clocked_in 
                            : teamClockResult.not_clocked_in}
                        </p>
                        <p className="text-xs text-slate-400">
                          {teamClockResult.type === 'clockin' ? t('supervisor.alreadyIn') : t('supervisor.notClockedIn')}
                        </p>
                      </div>
                      <div className="bg-[#1A2433] rounded-lg p-3">
                        <p className="text-2xl font-bold text-[#A78BFA]">
                          {teamClockResult.total_members}
                        </p>
                        <p className="text-xs text-slate-400">{t('supervisor.totalMembers')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Team Action Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={handleTeamClockIn}
                    disabled={isTeamLoading}
                    className="h-12 md:h-16 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-[#00C896] hover:to-emerald-700 text-white font-semibold text-sm md:text-base shadow-lg"
                  >
                    {isTeamLoading ? (
                      <Clock className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" />
                    ) : (
                      <Users className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    )}
                    {t('supervisor.clockInTeam')}
                  </Button>
                  <Button
                    onClick={handleTeamClockOut}
                    disabled={isTeamLoading}
                    className="h-12 md:h-16 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-semibold text-sm md:text-base shadow-lg"
                  >
                    {isTeamLoading ? (
                      <Clock className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" />
                    ) : (
                      <LogOut className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    )}
                    {t('supervisor.clockOutTeam')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-xl font-bold">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {activities.length === 0 ? (
                <p className="text-center py-8" style={{ color: '#64748B' }}>No recent activity</p>
              ) : (
                activities.map((activity, ai) => (
                  <div
                    key={activity.id || `activity-${ai}`}
                    className="p-3 bg-[#1A2433] border rounded-lg hover:border-[#2D3748] transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-white">{activity.action}</p>
                        {activity.jobNumber && (
                          <p className="text-sm text-[#1E88E5]">Job #{activity.jobNumber}</p>
                        )}
                      </div>
                      <p className="text-sm" style={{ color: '#64748B' }}>{activity.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick-note dialog — opened from the home "Note" tile */}
      <QuickHomeNoteDialog
        open={showHomeNote}
        onClose={() => setShowHomeNote(false)}
        token={token}
        currentUser={user}
      />

    </div>
  );
};

export default Home;